import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MobileMenu from "./MobileMenu";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import { LiaPhoneVolumeSolid } from "react-icons/lia";
import "bootstrap/dist/css/bootstrap.min.css";
import { HiClock } from "react-icons/hi2";
import { FaMapLocationDot } from "react-icons/fa6";
import { useRefContext } from "../../components/ScrollToAppointment";
import { useMediaQuery } from "@mui/material";
import { RiCalendarScheduleFill } from "react-icons/ri";

const routes = [
  { path: "/", key: "home" },
  { path: "/about", key: "about" },
  { path: "/services", key: "services", childPaths: ["/service-details"] },
  { path: "/equipments", key: "equipments" },
  { path: "/doctor", key: "doctor" },
  { path: "/blog", key: "blogs", childPaths: ["/blog-details"] },
  { path: "/contact", key: "contact" },
];

const Header5 = ({ onScrollToAppointment }) => {
  const [mobileTrigger, setMobileTrigger] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const isMobile = useMediaQuery("(max-width:1100px)");
  const isExtraSmall = useMediaQuery("(max-width:550px)");
  const { t } = useTranslation();
  const [showTopContainer, setShowTopContainer] = useState(true);
  const { scrollToAppointment } = useRefContext();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;

      if (scrollY > 100 && !isScrolled) {
        setIsScrolled(true);
        setShowTopContainer(false);
      } else if (scrollY <= 50 && isScrolled) {
        setIsScrolled(false);
        setShowTopContainer(true);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isScrolled]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 868) {
      } else {
      }
    };

    window.addEventListener("resize", handleResize);

    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const isRouteActive = (routePath, childPaths = []) => {
    if (location.pathname === routePath) {
      return true;
    }

    if (
      childPaths.some((childPath) => location.pathname.startsWith(childPath))
    ) {
      return true;
    }

    if (
      routePath === "/services" &&
      location.pathname.startsWith("/service-details")
    ) {
      return true;
    }
    if (
      routePath === "/blog" &&
      location.pathname.startsWith("/blog-details")
    ) {
      return true;
    }

    return false;
  };
  const headerStyles = {
    position: "sticky",
    top: isScrolled || isMobile ? 0 : 70,
    zIndex: 10,
    boxShadow: isScrolled
      ? "0 2px 8px rgba(0, 0, 0, 0.1)"
      : "0 2px 8px rgba(255, 255, 255, 0.1)",
    height: isScrolled ? "70px" : "110px",
    minHeight: "70px",
    transition:
      "background-color 0.1s ease, height 0.3s ease, position 0.1s ease, top 0.1s ease, box-shadow 0.3s ease",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: "0 15px",
    backgroundColor: isScrolled ? "white" : "var(--color-fourth)",
    color: isScrolled ? "var(--color-fourth)" : "white",
    width: "100%",
  };

  const logoStyles = {
    fontSize: isMobile ? (isExtraSmall ? "5.5vw" : "3vw") : "2vw",

    fontWeight: "bold",
    color: isScrolled ? "#333" : "white",

    textAlign: "center",
    height: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: "150px",
  };

  const hospitalNameStyles = {
    fontSize: isMobile ? (isExtraSmall ? "5.5vw" : "3vw") : "1.5vw",
    color: isScrolled ? "#333" : "white",
    marginLeft: isMobile ? "0" : "6px",
    textAlign: isMobile ? "center" : "initial",
    fontWeight: "normal",
  };

  const navStyles = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    height: "100%",
  };

  const navListStyles = {
    display: "flex",
    listStyle: "none",
    padding: 0,
    margin: 0,
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
  };

  const navItemStyles = {
    margin: "0 15px",
  };

  const linkStyles = (isActive) => ({
    fontSize: "1rem",
    transition:
      "color 0.1s ease, background-color 0.1s ease, padding 0.3s ease, border-bottom 0.3s ease",

    backgroundColor:
      isActive && isScrolled
        ? "white"
        : !isActive && isScrolled
        ? "white"
        : isActive && !isScrolled
        ? "white"
        : "var(--color-fourth)",

    color:
      isActive && isScrolled
        ? "var(--color-fourth)"
        : !isActive && isScrolled
        ? "var(--color-fourth)"
        : isActive && !isScrolled
        ? "var(--color-fourth)"
        : "white",

    fontWeight: isActive ? "1000" : "400",
    textDecoration: "none",
    padding: isActive ? "5px 20px" : "5px 10px",
    borderRadius: "5px",
    position: "relative",

    borderBottom:
      isActive && isScrolled ? "3px solid var(--color-fourth)" : "none",
    marginBottom: isActive && isScrolled ? "3px" : "0",
  });

  const hamburgerStyles = {
    display: isMobile ? "flex" : "none",
    flexDirection: "column",
    width: "30px",
    height: "25px",
    justifyContent: "space-between",
    background: "transparent",
    border: "none",
    padding: 0,
    cursor: "pointer",
    color: isMobile || isScrolled ? "white" : "var(--color-fourth)",
    zIndex: 100,
  };

  const phoneNumber = "918980863464";
  const message = "Hello, I need help!";
  const encodedMessage = encodeURIComponent(message);
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;

  const hamburgerLineStyles = {
    width: "100%",
    height: "4px",
    borderRadius: "2px",
    background: isMobile && !isScrolled ? "white" : "var(--color-fourth)",
    minWidth: "20px",
  };

  return (
    <>
      {!isMobile && showTopContainer && (
        <div
          style={{
            backgroundColor: "#ffffff",
            padding: "15px 0",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            width: "100%",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontSize: "0.95rem",
            color: "#333",
            height: "70px",
            transition: "top 0.3s ease-in-out",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              maxWidth: "90%",
              flexWrap: "wrap",
              alignItems: "center",
              textAlign: "center",
              marginRight: "14%",
              marginLeft: "14%",
            }}
          >
            <span
              style={{
                marginRight: "30px",
                fontWeight: "500",
                alignItems: "center",
              }}
            >
              <strong
                style={{
                  color: "var(--color-fourth)",
                  marginRight: "4px",
                }}
              >
                <HiClock fontSize={22} /> {t("header.openingHours")}:
              </strong>
              <span
                style={{
                  fontWeight: "normal",
                }}
              >
                {t("header.openingHoursValue")}
              </span>
            </span>

            <div
              style={{
                height: "30px",
                width: "1px",
                backgroundColor: "#e0e0e0",
              }}
            ></div>

            <span
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <strong
                style={{
                  color: "var(--color-fourth)",
                  marginRight: "4px",
                }}
              >
                <FaMapLocationDot fontSize={22} /> {t("header.address")}:
              </strong>
              {t("header.addressValue")}
            </span>

            <div
              style={{
                height: "30px",
                width: "1px",
                backgroundColor: "#e0e0e0",
                marginRight: "4px",
                marginLeft: "4px",
              }}
            ></div>

            <span
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <LiaPhoneVolumeSolid
                fontSize={22}
                style={{
                  marginRight: "8px",
                  color: "var(--color-fourth)",
                  fontWeight: "bolder",
                }}
              />
              <strong
                style={{
                  color: "var(--color-fourth)",
                  cursor: "pointer",
                  marginRight: "4px",
                }}
              >
                {t("header.contact")}:
              </strong>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontWeight: "bold",
                }}
              >
                {t("header.contactValue")}
              </a>
            </span>
          </div>
        </div>
      )}

      <header style={headerStyles}>
        <div style={navStyles}>
          {isMobile ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                flex: 1,
              }}
            >
              <span style={{ ...logoStyles, gap: "20px" }}>
                <span style={{ marginRight: "6px" }}> {t("home.name")}</span>
                <span style={hospitalNameStyles}>{t("home.hospital")}</span>
              </span>
            </div>
          ) : (
            <div
              style={{ flex: 2, cursor: "pointer" }}
              onClick={() => navigate("/")}
            >
              <span style={logoStyles}>
                {t("home.name")}
                <span style={hospitalNameStyles}>{t("home.hospital")}</span>
              </span>
            </div>
          )}
          {!isMobile && (
            <nav
              style={{
                flex: 3,
                height: "100%",
              }}
            >
              <ul style={navListStyles}>
                {routes.map(({ path, key, childPaths = [] }) => (
                  <li style={navItemStyles} key={key}>
                    <Link
                      to={path}
                      style={linkStyles(isRouteActive(path, childPaths))}
                    >
                      {t(`header.routes.${key}`)}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          )}
          <div
            style={{
              flex: 2,
              justifyContent: "end",
              display: "flex",
            }}
          >
            {!isExtraSmall && (
              <button
                onClick={scrollToAppointment}
                style={{
                  padding: !isMobile ? "8px 36px" : "8px 26px",
                  fontSize: "1rem",
                  backgroundColor: isScrolled
                    ? "var(--color-fourth)"
                    : "transparent",
                  color: "white",
                  border: "1px solid",
                  borderColor: isScrolled ? "transparent" : "white",
                  borderRadius: "5px",
                  cursor: "pointer",
                  transition: "0.3s",
                  marginRight: !isMobile ? "50px" : "30px",
                  marginLeft: !isMobile ? "0" : "20px",
                  maxHeight: "120px",
                  lineHeight: "1.5",
                }}
              >
                {isExtraSmall ? (
                  <RiCalendarScheduleFill />
                ) : (
                  t("header.getAppointment")
                )}
              </button>
            )}
          </div>
          {!isMobile && <LanguageSwitcher isMobile={isMobile} />}
          {isMobile && (
            <div style={{ minWidth: "50px" }}>
              <button
                onClick={() => setMobileTrigger(!mobileTrigger)}
                style={hamburgerStyles}
              >
                <span style={hamburgerLineStyles}></span>
                <span style={hamburgerLineStyles}></span>
                <span style={hamburgerLineStyles}></span>
              </button>
            </div>
          )}
        </div>
      </header>

      {mobileTrigger && (
        <MobileMenu
          mobileTrigger={mobileTrigger}
          setMobileTrigger={setMobileTrigger}
          isMobile={isMobile}
        />
      )}
    </>
  );
};

export default Header5;
